// src/pages/Dashboard/Admin/Requests/components/RequestFormDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  FaBug,
  FaExclamationTriangle,
  FaImage,
  FaSave,
  FaTimes,
} from "react-icons/fa";

import {
  QUILL_FORMATS,
  QUILL_MODULES,
  REQUEST_PRIORITIES,
  getDefaultRequestForm,
  getPriorityMeta,
  mapRequestToForm,
  validateRequestForm,
} from "../utils/requestsDashboardUtils";

function DrawerField({ label, children, hint, error }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      {children}

      {error ? (
        <p className="mt-1 text-xs leading-5 text-rose-600 dark:text-rose-300">
          {error}
        </p>
      ) : hint ? (
        <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export default function RequestFormDrawer({
  isOpen,
  mode,
  request,
  submitting,
  onClose,
  onSubmit,
}) {
  const [formData, setFormData] = useState(getDefaultRequestForm());

  useEffect(() => {
    if (!isOpen) return;

    setFormData(
      mode === "edit" ? mapRequestToForm(request) : getDefaultRequestForm()
    );
  }, [isOpen, mode, request]);

  const errors = useMemo(() => validateRequestForm(formData), [formData]);
  const canSubmit = Object.keys(errors).length === 0 && !submitting;

  const priority = getPriorityMeta(formData.priority);

  const title =
    mode === "edit" ? "Modifier la requête" : "Nouvelle requête support";

  const submitLabel =
    mode === "edit" ? "Enregistrer les modifications" : "Envoyer la requête";

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    onSubmit?.(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9998]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Support
            </p>

            <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
              <FaBug className="text-indigo-600 dark:text-indigo-300" />
              {title}
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Décrivez précisément le bug ou la fonctionnalité souhaitée. Pour
              un bug, indiquez la page concernée, les étapes pour reproduire et
              le navigateur utilisé.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
            <DrawerField label="Titre *" error={errors.title}>
              <input
                type="text"
                value={formData.title}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    title: event.target.value,
                  }))
                }
                placeholder="Ex : Erreur lors de l’export des adhérents"
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
              />
            </DrawerField>

            <DrawerField
              label="Priorité"
              hint="Urgent uniquement pour un blocage important ou une erreur empêchant l’utilisation."
            >
              <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
                <select
                  value={formData.priority}
                  onChange={(event) =>
                    setFormData((prev) => ({
                      ...prev,
                      priority: event.target.value,
                    }))
                  }
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  {REQUEST_PRIORITIES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>

                <span
                  className={`inline-flex items-center justify-center gap-2 rounded-2xl px-3 py-2.5 text-xs font-black ring-1 ${priority.badge}`}
                >
                  <FaExclamationTriangle />
                  {priority.label}
                </span>
              </div>
            </DrawerField>

            <DrawerField
              label="Description détaillée *"
              error={errors.description}
              hint="Vous pouvez intégrer une image via l’éditeur, mais le vrai upload de captures nécessitera une route serveur dédiée."
            >
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
                <ReactQuill
                  value={formData.description}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: value,
                    }))
                  }
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  placeholder="Décrivez la demande, le contexte, les étapes, le résultat attendu..."
                  className="[&_.ql-container]:min-h-[260px] [&_.ql-container]:max-h-[480px] [&_.ql-container]:overflow-y-auto dark:[&_.ql-toolbar]:border-slate-700 dark:[&_.ql-container]:border-slate-700"
                />
              </div>
            </DrawerField>

            <div className="rounded-[24px] border border-amber-200 bg-amber-50 p-4 dark:border-amber-500/20 dark:bg-amber-500/10">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-amber-700 shadow-sm ring-1 ring-amber-200 dark:bg-slate-900 dark:text-amber-300 dark:ring-amber-500/20">
                  <FaImage />
                </div>

                <div>
                  <p className="text-sm font-bold text-slate-900 dark:text-white">
                    Captures d’écran
                  </p>

                  <p className="mt-1 text-xs leading-5 text-slate-700 dark:text-slate-300">
                    Le formulaire actuel permet surtout une description texte.
                    Pour envoyer de vraies captures, il faudra ajouter un module
                    de pièces jointes côté serveur. En attendant, l’utilisateur
                    peut coller un lien d’image ou intégrer une image via
                    l’éditeur si elle est déjà hébergée.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
              >
                <FaSave />
                {submitting ? "Enregistrement..." : submitLabel}
              </button>
            </div>
          </div>
        </form>
      </aside>
    </div>
  );
}